import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogIcon,
  DialogTitle,
} from "@/components/ui/dialog";
import { CircleNotch, Question, Warning } from "@phosphor-icons/react";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "destructive" gets a red warning treatment, "default" stays neutral. */
  tone?: "default" | "destructive";
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = "Potvrdi",
  cancelLabel = "Nazad",
  tone = "default",
  isPending = false,
  onClose,
  onConfirm,
}: ConfirmDialogProps) {
  if (!open) return null;
  const destructive = tone === "destructive";
  const Icon = destructive ? Warning : Question;

  return (
    <Dialog open onClose={() => !isPending && onClose()} className="sm:max-w-sm">
      <DialogHeader onClose={isPending ? undefined : onClose}>
        <div className="flex items-center gap-3">
          <DialogIcon tone={destructive ? "destructive" : "default"}>
            <Icon className="size-5" weight="duotone" />
          </DialogIcon>
          <DialogTitle>{title}</DialogTitle>
        </div>
      </DialogHeader>

      {description && (
        <DialogBody>
          <p className="text-sm text-muted-foreground">{description}</p>
        </DialogBody>
      )}

      <DialogFooter className={description ? undefined : "border-t-0 pt-0 sm:pt-0"}>
        <Button variant="outline" onClick={onClose} disabled={isPending} className="bg-transparent">
          {cancelLabel}
        </Button>
        <Button variant={destructive ? "destructive" : "default"} onClick={onConfirm} disabled={isPending}>
          {isPending && <CircleNotch className="size-4 animate-spin" />}
          {confirmLabel}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
